// Betrieb › Tarife (h-ui): Tarife als Karten, anlegen und bearbeiten.
//
// Grenzen sind schlichte Felder – leer heißt unbegrenzt. Module: leer = alle
// erlaubt. Genau ein Tarif ist die Vorgabe für neue Radios.

import { useEffect, useState } from "react";
import { GRENZEN_NAMEN, api, betriebApi } from "../../api";
import { useAuth } from "../../auth/AuthProvider";
import { errorText } from "../../components/streaming-ui";
import { Meldungen, Sym } from "../../components/social-ui";
import { Badge, Button, Callout, Card, EmptyState, Page, PageHeader, Skeleton } from "../../components/ui";
import { GRENZEN_FELDER, grenzeText } from "../../components/hosting-ui";
import type { Grenzen, Tarif } from "../../types";
import { NurBetreiber } from "./NurBetreiber";

type Modul = { id: string; label: string };

export function TarifePage(): JSX.Element {
  const { user } = useAuth();
  const [tarife, setTarife] = useState<Tarif[] | null>(null);
  const [module, setModule] = useState<Modul[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  // null = zu, "neu" = neuer Tarif, sonst key des Tarifs
  const [bearbeiten, setBearbeiten] = useState<string | null>(null);

  const laden = (): void => {
    betriebApi.tarife().then(setTarife).catch((err) => { setTarife([]); setFehler(errorText(err)); });
  };

  useEffect(() => {
    if (user?.role !== "super_admin") return;
    laden();
    api.listModules().then((l) => setModule(l.map((m) => ({ id: m.id, label: m.label })))).catch(() => undefined);
  }, [user?.role]);

  if (user?.role !== "super_admin") return <NurBetreiber titel="Tarife" />;

  const melde = (t: string): void => { setHinweis(t); setFehler(null); window.setTimeout(() => setHinweis(null), 4000); };

  const loeschen = async (t: Tarif): Promise<void> => {
    if (!window.confirm(`Tarif „${t.name}“ löschen?`)) return;
    try {
      await betriebApi.tarifLoeschen(t.key);
      melde("Tarif gelöscht.");
      laden();
    } catch (err) {
      setFehler(errorText(err));
    }
  };

  const aktuell = bearbeiten && bearbeiten !== "neu" ? tarife?.find((t) => t.key === bearbeiten) ?? null : null;

  return (
    <Page>
      <PageHeader
        eyebrow="Betrieb"
        title="Tarife"
        description="Was ein Radio darf: Kanäle, Speicher, Hörer, Funktionen. Individuelle Abweichungen stellst du am Radio ein."
        actions={
          <Button variant="primary" onClick={() => setBearbeiten("neu")} disabled={bearbeiten === "neu"}>
            <Sym name="plus" /> Neuer Tarif
          </Button>
        }
      />
      <Meldungen error={fehler} notice={hinweis} onClear={() => setHinweis(null)} />

      {bearbeiten && (
        <TarifFormular
          key={bearbeiten}
          tarif={aktuell}
          module={module}
          vergeben={(tarife ?? []).map((t) => t.key)}
          onAbbrechen={() => setBearbeiten(null)}
          onGespeichert={() => { setBearbeiten(null); melde("Tarif gespeichert."); laden(); }}
        />
      )}

      {tarife === null && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-72" />)}
        </div>
      )}
      {tarife?.length === 0 && (
        <div className="card p-2">
          <EmptyState icon={<Sym name="list" className="h-5 w-5" />} title="Noch keine Tarife" description="Leg den ersten Tarif an." />
        </div>
      )}
      {tarife && tarife.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tarife.map((t) => (
            <section key={t.key} className={`card flex flex-col ${t.aktiv ? "" : "opacity-70"}`}>
              <div className="card-header items-start">
                <div className="min-w-0">
                  <h2 className="card-title flex flex-wrap items-center gap-2">
                    {t.name}
                    {t.vorgabe && <Badge tone="brand">Vorgabe</Badge>}
                    {!t.aktiv && <Badge>nicht mehr wählbar</Badge>}
                  </h2>
                  <p className="mt-0.5 font-mono text-xs text-muted">{t.key}</p>
                </div>
                {t.radios != null && <Badge tone={t.radios > 0 ? "info" : "neutral"}>{t.radios} {t.radios === 1 ? "Radio" : "Radios"}</Badge>}
              </div>
              <div className="card-body flex flex-1 flex-col">
                {t.beschreibung && <p className="mb-3 text-sm text-secondary">{t.beschreibung}</p>}
                {t.preis_hinweis && <p className="mb-3 text-sm font-medium text-primary">{t.preis_hinweis}</p>}
                <dl className="space-y-1.5 text-sm">
                  {GRENZEN_FELDER.map((f) => (
                    <div key={f.key} className="flex justify-between gap-3">
                      <dt className="min-w-0 truncate text-muted">{GRENZEN_NAMEN[f.key]}</dt>
                      <dd className={`flex-shrink-0 tabular-nums ${t.grenzen[f.key] === false ? "text-muted" : "text-primary"}`}>{grenzeText(f.key, t.grenzen)}</dd>
                    </div>
                  ))}
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Mitmach-Module</dt>
                    <dd className="min-w-0 truncate text-right text-primary" title={t.module.join(", ")}>
                      {t.module.length === 0 ? "alle" : `${t.module.length} ausgewählt`}
                    </dd>
                  </div>
                </dl>
                <div className="mt-auto flex flex-wrap gap-2 pt-4">
                  <Button size="sm" onClick={() => { setBearbeiten(t.key); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Bearbeiten</Button>
                  <Button size="sm" variant="ghost" disabled={(t.radios ?? 0) > 0 || t.vorgabe}
                    title={(t.radios ?? 0) > 0 ? "Wird noch von Radios genutzt" : t.vorgabe ? "Die Vorgabe lässt sich nicht löschen" : undefined}
                    onClick={() => void loeschen(t)}>
                    Löschen
                  </Button>
                </div>
              </div>
            </section>
          ))}
        </div>
      )}
    </Page>
  );
}

function TarifFormular({ tarif, module, vergeben, onAbbrechen, onGespeichert }: {
  tarif: Tarif | null;
  module: Modul[];
  vergeben: string[];
  onAbbrechen: () => void;
  onGespeichert: () => void;
}): JSX.Element {
  const neu = !tarif;
  const [key, setKey] = useState(tarif?.key ?? "");
  const [name, setName] = useState(tarif?.name ?? "");
  const [beschreibung, setBeschreibung] = useState(tarif?.beschreibung ?? "");
  const [preis, setPreis] = useState(tarif?.preis_hinweis ?? "");
  const [sort, setSort] = useState(String(tarif?.sort ?? 0));
  const [aktiv, setAktiv] = useState(tarif?.aktiv ?? true);
  const [vorgabe, setVorgabe] = useState(tarif?.vorgabe ?? false);
  const [werte, setWerte] = useState<Record<string, string>>(() => {
    const w: Record<string, string> = {};
    for (const f of GRENZEN_FELDER) {
      const v = tarif?.grenzen[f.key];
      w[f.key] = f.art === "zahl" ? (v == null ? "" : String(v)) : v === false ? "nein" : "ja";
    }
    return w;
  });
  const [alleModule, setAlleModule] = useState((tarif?.module.length ?? 0) === 0);
  const [gewaehlt, setGewaehlt] = useState<string[]>(tarif?.module ?? []);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const keyOk = !neu || (/^[a-z0-9][a-z0-9_-]{1,31}$/.test(key) && !vergeben.includes(key));
  const zahlFehler = GRENZEN_FELDER.some((f) => f.art === "zahl" && werte[f.key]!.trim() !== "" && !(Number(werte[f.key]!.replace(",", ".")) >= 0));
  const bereit = keyOk && name.trim().length > 0 && !zahlFehler && (alleModule || gewaehlt.length > 0);

  const speichern = async (): Promise<void> => {
    const grenzen: Grenzen = {};
    for (const f of GRENZEN_FELDER) {
      const w = werte[f.key]!;
      (grenzen as Record<string, unknown>)[f.key] = f.art === "zahl"
        ? (w.trim() === "" ? null : Number(w.replace(",", ".")))
        : w === "ja";
    }
    const daten = {
      name: name.trim(),
      beschreibung: beschreibung.trim(),
      preis_hinweis: preis.trim() || null,
      grenzen,
      module: alleModule ? [] : gewaehlt,
      aktiv,
      sort: Number(sort) || 0,
      vorgabe,
    };
    setLaeuft(true);
    setFehler(null);
    try {
      if (neu) await betriebApi.tarifAnlegen({ key, ...daten });
      else await betriebApi.tarifAendern(tarif.key, daten);
      onGespeichert();
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Card title={neu ? "Neuer Tarif" : `Tarif „${tarif.name}“ bearbeiten`} className="mb-6 animate-fade-in">
      {fehler && <Callout tone="danger" className="mb-4">{fehler}</Callout>}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="field-label">Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Verein" />
        </label>
        <label className="block">
          <span className="field-label">Schlüssel</span>
          <input className="input font-mono" value={key} disabled={!neu} onChange={(e) => setKey(e.target.value.toLowerCase())} placeholder="verein" />
          <span className={`field-hint block ${neu && key && !keyOk ? "!text-red-600 dark:!text-red-400" : ""}`}>
            {neu ? (vergeben.includes(key) ? "Diesen Schlüssel gibt es schon." : "Kleinbuchstaben, Ziffern, - und _. Später nicht änderbar.") : "Nicht änderbar – Radios hängen daran."}
          </span>
        </label>
        <label className="block sm:col-span-2">
          <span className="field-label">Beschreibung</span>
          <textarea className="input" rows={2} value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} placeholder="Für wen ist der Tarif gedacht?" />
        </label>
        <label className="block">
          <span className="field-label">Preis-Hinweis <span className="font-normal text-muted">(nur Anzeige)</span></span>
          <input className="input" value={preis} onChange={(e) => setPreis(e.target.value)} placeholder="z. B. 19 € im Monat" />
        </label>
        <label className="block">
          <span className="field-label">Reihenfolge</span>
          <input className="input" inputMode="numeric" value={sort} onChange={(e) => setSort(e.target.value)} />
        </label>
      </div>

      <div className="section-label mb-2 mt-6">Grenzen</div>
      <p className="mb-3 text-xs text-muted">Leeres Feld = unbegrenzt.</p>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        {GRENZEN_FELDER.map((f) => (
          <label key={f.key} className="block">
            <span className="field-label">{GRENZEN_NAMEN[f.key]}</span>
            {f.art === "zahl" ? (
              <div className="flex items-center gap-2">
                <input className="input min-w-0" inputMode="decimal" placeholder="unbegrenzt" value={werte[f.key]}
                  onChange={(e) => setWerte((w) => ({ ...w, [f.key]: e.target.value }))} />
                <span className="w-14 flex-shrink-0 text-xs text-muted">{f.einheit}</span>
              </div>
            ) : (
              <select className="input" value={werte[f.key]} onChange={(e) => setWerte((w) => ({ ...w, [f.key]: e.target.value }))}>
                <option value="ja">enthalten</option>
                <option value="nein">nicht enthalten</option>
              </select>
            )}
            <span className="field-hint block">{f.hilfe}</span>
          </label>
        ))}
      </div>
      {zahlFehler && <p className="mt-2 text-xs text-red-600 dark:text-red-400">Bitte nur Zahlen ab 0 – oder leer für „unbegrenzt“.</p>}

      <div className="section-label mb-2 mt-6">Mitmach-Module</div>
      <label className="flex items-center gap-2 text-sm text-primary">
        <input type="checkbox" className="h-4 w-4" checked={alleModule} onChange={(e) => setAlleModule(e.target.checked)} />
        Alle Module erlaubt
      </label>
      {!alleModule && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {module.map((m) => (
            <label key={m.id} className="flex items-center gap-2 rounded-lg border border-default px-3 py-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={gewaehlt.includes(m.id)}
                onChange={(e) => setGewaehlt((g) => e.target.checked ? [...g, m.id] : g.filter((x) => x !== m.id))} />
              <span className="min-w-0 truncate">{m.label}</span>
            </label>
          ))}
          {gewaehlt.length === 0 && <p className="text-xs text-red-600 dark:text-red-400 sm:col-span-2">Mindestens ein Modul wählen – oder „Alle Module erlaubt“.</p>}
        </div>
      )}

      <div className="mt-6 flex flex-col gap-2 text-sm sm:flex-row sm:gap-6">
        <label className="flex items-center gap-2">
          <input type="checkbox" className="h-4 w-4" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
          Für neue Radios wählbar
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" className="h-4 w-4" checked={vorgabe} disabled={tarif?.vorgabe} onChange={(e) => setVorgabe(e.target.checked)} />
          Vorgabe für neue Radios
        </label>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button variant="primary" disabled={!bereit || laeuft} onClick={() => void speichern()}>{laeuft ? "Speichert …" : "Speichern"}</Button>
        <Button variant="ghost" onClick={onAbbrechen}>Abbrechen</Button>
      </div>
    </Card>
  );
}
